import React, { useState, useEffect } from 'react';
import { fetchReviews } from '../api';
import ReviewCard from '../components/ReviewCard';
import Loader from '../components/Loader';

const STATUS_FILTERS = ['⏰ Recent', '👻 Ghosted', '✅ Received', '❌ Rejected', '⏳ In Progress'];

export default function CompanyReviewsPage({ companyName, roleName, locationName, setView, user, backView = 'companies' }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('⏰ Recent');

  useEffect(() => {
    if (!companyName) return;
    setLoading(true);
    setReviews([]);
    setStatusFilter('⏰ Recent');

    // Fetch all recent reviews and filter by company in-memory
    // avoids the Firestore composite index required by where+orderBy
    fetchReviews({ limit: 500 })
      .then(data => {
        const all = data.reviews || [];
        setReviews(all.filter(r => {
          const roleMatch = roleName
            ? (r.role || '').toLowerCase() === roleName.toLowerCase()
            : (r.companyName || '').toLowerCase() === companyName.toLowerCase();
          const locationMatch = locationName
            ? (r.location || '').toLowerCase().includes(locationName.toLowerCase())
            : true;
          return roleMatch && locationMatch;
        }));
      })
      .catch(err => {
        console.error('Failed to load company reviews:', err);
        setReviews([]);
      })
      .finally(() => setLoading(false));
  }, [companyName, roleName, locationName]);

  const filtered = reviews.filter(r => {
    if (statusFilter === '⏰ Recent') return true;
    if (statusFilter === '👻 Ghosted') return r.status === 'ghosted';
    if (statusFilter === '✅ Received') return r.status === 'offer';
    if (statusFilter === '❌ Rejected') return r.status === 'rejected';
    if (statusFilter === '⏳ In Progress') return r.status === 'ongoing';
    return true;
  });

  // Quick stats from loaded reviews
  const ghostCount = reviews.filter(r => r.status === 'ghosted').length;
  const offerCount = reviews.filter(r => r.status === 'offer').length;
  const completed = reviews.filter(r => r.status !== 'ongoing').length;
  const ghostPct = completed > 0 ? Math.round((ghostCount / completed) * 100) : 0;
  const offerPct = completed > 0 ? Math.round((offerCount / completed) * 100) : 0;

  return (
    <div style={{ animation: 'fadeUp 0.4s ease-out', position: 'relative' }}>
      
      {/* Absolute Back Arrow */}
      <button
        onClick={() => setView(backView)}
        style={{
          position: 'absolute', left: 0, top: -24,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: 8, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 13, fontWeight: 700, transition: 'all 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.color = 'var(--blue)'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--text2)'}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>

      {/* Page Header */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
        padding: '10px 14px', marginBottom: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Company avatar — only shown in company-focused view, not role view */}
          {!roleName && (
            <div style={{
              width: 36, height: 36, borderRadius: 8, flexShrink: 0,
              background: 'linear-gradient(135deg, var(--blue), #0284c7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, color: '#fff', fontWeight: 900,
            }}>
              {companyName?.charAt(0)?.toUpperCase()}
            </div>
          )}

          {/* Title + Stats inline */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{
                fontFamily: 'var(--display)', fontSize: 16, fontWeight: 900,
                color: 'var(--text)', letterSpacing: -0.3,
              }}>
                {roleName || companyName}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500, marginTop: 2 }}>
                {loading ? 'Loading...' : `${reviews.length} kahanis${roleName ? ' for this role' : ` at ${companyName}`}${locationName ? ` in ${locationName}` : ''}`}
              </div>
            </div>

            {!loading && reviews.length > 0 && (
              <div style={{ display: 'flex', gap: 16, padding: '0 8px', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 800, color: ghostPct > 20 ? 'var(--red)' : 'var(--green)' }}>
                    {ghostPct}%
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Ghost</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 800, color: offerPct > 40 ? 'var(--green)' : 'var(--blue)' }}>
                    {offerPct}%
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Offer</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Row: Status + Company Focus */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
          {STATUS_FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              style={{
                padding: '5px 14px', borderRadius: 100, fontSize: 11, fontWeight: 700,
                border: `1px solid ${statusFilter === f ? 'var(--blue)' : 'var(--border2)'}`,
                background: statusFilter === f ? 'var(--blue)' : 'var(--surface)',
                color: statusFilter === f ? '#fff' : 'var(--text2)',
                cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                fontFamily: 'var(--sans)'
              }}
            >
              {f}
            </button>
          ))}
        </div>

      </div>

      {/* Reviews list */}
      {loading ? (
        <Loader inline message={`Loading ${roleName || companyName} kahanis...`} />
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)', fontSize: 14 }}>
          {statusFilter === '⏰ Recent'
            ? 'No kahanis found for this role yet.'
            : `No ${statusFilter} kahanis for this role.`}
        </div>
      ) : (
        filtered.map((r, i) => (
          <ReviewCard key={r.id || r._id || i} review={r} delay={i * 0.04} currentUser={user} />
        ))
      )}
    </div>
  );
}
